import { useContext, useEffect, useState } from 'react';
import { gql, GraphQLClient } from 'graphql-request';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { clone, Font, Style, USER_ROLE } from '../constants';
import { StyleBuilder } from '../components/article/StyleBuilder';
import { Logger } from '../utils/Logger';
import { AppContext } from '../App.context';
import { QueryRequest } from '@/services/query-client/QueryClient';
import {
	getHTTPHeaders,
	GRAPHQL_URL,
} from '@/views/home/auth/Authentication.hooks';
import { useIsOnline } from '@/hooks';

const logger = new Logger('Assets', {
	log: 'rgb(50, 173, 230)',
	debug: 'rgb(0, 113, 164)',
	info: 'rgb(36, 138, 61)',
	error: 'rgb(255, 45, 85)',
});

export function useAssets(): AssetsResponse {
	const queryKey = ['assets'];
	const isOnline = useIsOnline();
	const queryClient = useQueryClient();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { parseGraphQLClientError, queryRequest, db } =
		useContext(AppContext);

	const cached: AssetsQuery | undefined = queryClient.getQueryData(queryKey);

	useEffect(() => {
		if (isOnline) {
			queryClient.invalidateQueries({ queryKey });
			queryClient.removeQueries({ queryKey });
		}
	}, [isOnline]);

	const [fonts, setFonts] = useState<Array<Font>>([]);
	let cacheTime = isAdmin ? 0 : 1000 * 60 * 30; // 30 minutes
	if (cached && !isAdmin) {
		if (!cached.fonts.length) {
			cacheTime = 0;
		} else {
			cacheTime = 1000 * 60 * 30;
		}
	}

	const staleTime = cacheTime;

	const { data, isPending, error } = useQuery<AssetsQuery>({
		queryKey,
		queryFn: async () =>
			getAssets({ queryRequest, queryKey, queryClient, isOnline }),
		staleTime,
		gcTime: isOnline ? cacheTime : Infinity,
		retry: isOnline ? 3 : 0,
	});

	useEffect(() => {
		if (isPending) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;

		const processedFonts = data.fonts.map(mapFontUrls);

		logger.debug(
			`Total of fonts: ${processedFonts.length}\n`,
			processedFonts,
		);

		setFonts(processedFonts);
	}, [isPending, data, error]);
	return {
		fonts,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

async function getAssets({
	queryRequest,
	queryClient,
	queryKey,
	isOnline,
}: {
	queryRequest: QueryRequest;
	queryClient: QueryClient;
	queryKey: Array<any>;
	isOnline: boolean;
}): Promise<AssetsQuery> {
	const cached: AssetsQuery | undefined = queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached)
			return {
				fonts: [],
			};
		return cached;
	}

	// The app is online i will create a graphql client and make the request
	const headers = getHTTPHeaders(false);
	const client = new GraphQLClient(GRAPHQL_URL, { headers });

	return await queryRequest.queryWithClient(client, assetsQueryDocument);
}

interface AssetsQuery {
	fonts: Font[];
}

export function useLoadFonts(fonts: Array<Font>) {
	const promises: Array<Promise<void>> = [];
	useEffect(() => {
		if (!fonts.length) return;
		for (const font of fonts) {
			promises.push(loadFont(font));
		}
		Promise.all(promises)
			.then(() => {
				logger.info(`Success loading fonts`);
			})
			.catch(logger.error);
	}, [fonts]);
}

function mapFontUrls(f: Font) {
	const font = clone(f) as Font;
	font.urls = font.urls.map((url) =>
		url.replace(
			/(https:\/\/cdn\.pwa\.canvasflow\.io\/fonts)+/gim,
			'https://fonts.canvasflow.work',
		),
	);
	return font;
}

interface AssetsResponse {
	fonts: Array<Font>;
	isLoading: boolean;
	error: Error | null;
}

async function loadFont(font: Font): Promise<void> {
	const { name, urls } = font;
	const promises = [];

	for (const url of urls) {
		if (!/.woff2$/.test(url)) {
			continue;
		}
		// THIS DOWNLOADS THE FONT

		promises.push(
			new Promise<void>((resolve) => {
				const fontFace = new FontFace(
					name,
					`url(${url}) format("woff2")`,
				);
				fontFace
					.load()
					.then((loadedFace) => {
						document.fonts.add(loadedFace);
						resolve();
					})
					.catch((err) => {
						console.error(err);
						resolve();
					});
			}),
		);
	}
	await Promise.all(promises);
}

export const assetsQueryDocument = gql`
	query GetAssets {
		fonts {
			name
			urls
		}
	}
`;
